import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import EmailIcon from '@mui/icons-material/Email';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { EmailTemplate, listEmailTemplates } from '../services/emailApi';
import EmailTemplateRow from './EmailTemplateRow';
import SendEmailDialog from './SendEmailDialog';

interface EmailTemplateListProps {
  onBack: () => void;
  onCreate: () => void;
  onEdit: (template: EmailTemplate) => void;
}

export default function EmailTemplateList({
  onBack,
  onCreate,
  onEdit,
}: EmailTemplateListProps) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<EmailTemplate | null>(null);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const ROWS_PER_PAGE_OPTIONS = [10, 15, 20];

  // Case-insensitive substring match on the template name. Trimmed so trailing
  // whitespace from the input doesn't accidentally hide everything.
  const filteredTemplates = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return templates;
    return templates.filter((t) => (t.name || '').toLowerCase().includes(query));
  }, [templates, search]);

  // Reset to the first page whenever the filter changes so the user doesn't
  // land on an empty page when their query shrinks the result set.
  useEffect(() => {
    setPage(0);
  }, [search]);

  const pageStart = page * rowsPerPage;
  const visibleTemplates = filteredTemplates.slice(pageStart, pageStart + rowsPerPage);

  const handleSendClick = (template: EmailTemplate) => {
    setActiveTemplate(template);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listEmailTemplates();
      setTemplates(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
      <Button
        variant="text"
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Back to methods
      </Button>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <EmailIcon sx={{ color: '#1976D2', fontSize: 32 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Email templates
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Create reusable HTML templates and broadcast them via Elastic Email.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreate}
            sx={{ textTransform: 'none', bgcolor: '#1976D2', '&:hover': { bgcolor: '#115293' } }}
          >
            Create template
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        size="small"
        placeholder="Search by template name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        sx={{ mb: 2, maxWidth: 420 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                <TableCell sx={{ fontWeight: 700 }}>Template name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created at</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last modified</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && templates.length === 0 && !error && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No templates yet. Click <strong>Create template</strong> to add one.
                  </TableCell>
                </TableRow>
              )}
              {!loading && templates.length > 0 && filteredTemplates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No templates match <strong>{search}</strong>.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                visibleTemplates.map((template) => (
                  <EmailTemplateRow
                    key={template.id}
                    template={template}
                    onSend={handleSendClick}
                    onEdit={onEdit}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && filteredTemplates.length > ROWS_PER_PAGE_OPTIONS[0] && (
          <TablePagination
            component="div"
            count={filteredTemplates.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        )}
      </Paper>

      <SendEmailDialog
        open={dialogOpen}
        template={activeTemplate}
        onClose={handleDialogClose}
        onSent={() => {
          // Refresh list after a successful schedule.
          load();
        }}
      />
    </Box>
  );
}
